import { STATUSPAGE } from '@repo/ui';
import { GetAllCoursesUserUseCaseInterface } from 'domain/services/courses/get-all-courses-user';
import {
  GetAllGroupsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  GetActivitiesInProgressCountGroupedByOrganizationUseCaseInterface,
} from 'domain/usecases';
import { GetAllCurricularUnitsUserUseCaseInterface } from 'domain/usecases/curricular-units';
import {
  GetAllCHMetrics,
  GetSummaryProfile,
  GetAllUsersMetrics,
  GetActivityByStatus,
  GetAllActivitiesByTask,
  GetAllActivitiesByGroup,
  GetCompletedAndMissedActivitiesCount,
  GetActivitiesInProgressCount,
  GetAllActivitiesByPlace,
  GetAllActivitiesByStatus,
  GetAllActivitiesByStudent,
  ListOnlineUsersPerOrganizationIndicators,
  ListLastRegistrationApp,
  ListUsersOnlinePlatform,
  GetMapActivitiesMarkers,
  GetMapInternshipMarkers,
  GetMapActivityDetails,
} from 'domain/usecases/dashboard';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadCurricularUnitsUser } from 'presentation/config/store/curricular-units/curricular-units-slice';
import {
  loadActivityByStatus,
  loadingStatusPageActivityByStatus,
} from 'presentation/config/store/dashboard/dashboard-get-activity-by-status';
import { loadDashboardSummary } from 'presentation/config/store/dashboard/dashboard-get-summary.slice';
import {
  loadCHMetrics,
  loadUsersMetrics,
  loadActivitiesByTask,
  loadActivitiesByGroup,
  loadCompletedAndMissedActivitiesCount,
  loadActivitiesInProgressCount,
  loadActivitiesByPlace,
  loadActivitiesByStatus,
  loadActivitiesByStudent,
  loadMapActivityDetails,
  loadMapActivitiesMarkers,
  loadMapInternshipMarkers,
} from 'presentation/config/store/dashboard/dashboard-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { loadSemestersUser } from 'presentation/config/store/semesters/semesters-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

interface IProps {
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllSemestersUser?: GetAllSemestersUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
  getAllCurricularUnitsUser?: GetAllCurricularUnitsUserUseCaseInterface;
}

export const useDashboard = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllSemestersUser,
  getAllOrganizationsUser,
  getAllCurricularUnitsUser,
}: IProps) => {
  const dispatch = useDispatch();
  const { dashboardService, activityService } = useService();

  const [requestsStatus, setRequestsStatus] = useState({
    statusCH: STATUSPAGE.DONE,
    statusActivitiesByTask: STATUSPAGE.DONE,
    statusCompletedAndMissedActivitiesCount: STATUSPAGE.DONE,
    statusActivitiesInProgressCount: STATUSPAGE.DONE,
    statusActivitiesByGroup: STATUSPAGE.DONE,
    statusActivitiesByPlace: STATUSPAGE.DONE,
    statusDashboardIndicatorsUsersOnline: STATUSPAGE.DONE,
    statusLastRegistrationApp: STATUSPAGE.DONE,
    statusActivitiesByStudent: STATUSPAGE.DONE,
    statusPageActivitiesByStatus: STATUSPAGE.DONE,
    statusPageActivitiesAllByStatus: STATUSPAGE.DONE,
    statusListUsersOnlinePerPlatform: STATUSPAGE.DONE,
    organizationsUser: STATUSPAGE.DONE,
    coursesUser: STATUSPAGE.DONE,
    curricularUnitsUser: STATUSPAGE.DONE,
    semestersUser: STATUSPAGE.DONE,
    groupsUser: STATUSPAGE.DONE,
    statusGetActivitiesInProgressCountGroupedByOrganization: STATUSPAGE.DONE,
    statusMapActivitiesMarkers: STATUSPAGE.DONE,
    statusMapInternshipMarkers: STATUSPAGE.DONE,
    statusMapActivityDetails: STATUSPAGE.DONE,
  });

  const [loadingSummaryProfile, setLoadingSummaryProfile] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [
    saveOnlineUsersDashboardIndicators,
    setSaveOnlineUsersDashboardIndicators,
  ] = useState<ListOnlineUsersPerOrganizationIndicators.Result>();

  const [saveLastRegistrationApp, setSaveLastRegistrationApp] =
    useState<ListLastRegistrationApp.Result>();

  const [saveUsersOnlinePerPlatform, setSaveUsersOnlinePerPlatform] =
    useState<ListUsersOnlinePlatform.Result>({
      appProfessional: 0,
      appStudent: 0,
      web: 0,
    });

  const changeRequestStatus = (
    key: keyof typeof requestsStatus,
    status: STATUSPAGE,
  ) => {
    setRequestsStatus((prevState) => ({
      ...prevState,
      [key]: status,
    }));
  };

  const { organizationsUser } = useAppSelector((state) => state.Organizations);
  const { curricularUnitsUser } = useAppSelector(
    (state) => state.CurricularUnits,
  );

  const [
    responseActivityListingInProgress,
    setResponseActivityListingInProgress,
  ] =
    useState<GetActivitiesInProgressCountGroupedByOrganizationUseCaseInterface.Result>(
      {
        data: [{ x: '', y: 0 }],
      },
    );

  const requestGetAllOrganizationsUser = useCallback(() => {
    changeRequestStatus('organizationsUser', STATUSPAGE.LOADING);
    getAllOrganizationsUser
      .execute()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        changeRequestStatus('organizationsUser', STATUSPAGE.DONE);
      });
  }, []);

  const requestGetAllCoursesUser = useCallback((entityId?: string) => {
    changeRequestStatus('coursesUser', STATUSPAGE.LOADING);
    getAllCoursesUser
      .execute({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        changeRequestStatus('coursesUser', STATUSPAGE.DONE);
      });
  }, []);

  const requestGetAllCurricularUnitsUser = useCallback(
    (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      changeRequestStatus('curricularUnitsUser', STATUSPAGE.LOADING);
      getAllCurricularUnitsUser
        .execute(params)
        .then((res) => dispatch(loadCurricularUnitsUser(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('curricularUnitsUser', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllSemestersUser = useCallback((entityId: string) => {
    changeRequestStatus('semestersUser', STATUSPAGE.LOADING);
    getAllSemestersUser
      .execute({ entityId })
      .then((res) => dispatch(loadSemestersUser(res)))
      .catch((err) => console.log(err))
      .finally(() => {
        changeRequestStatus('semestersUser', STATUSPAGE.DONE);
      });
  }, []);

  const requestGetAllGroupsUser = useCallback(
    (params: GetAllGroupsUserUseCaseInterface.Params) => {
      changeRequestStatus('groupsUser', STATUSPAGE.LOADING);
      getAllGroupsUser
        .execute(params)
        .then((res) => dispatch(loadGroupsUser(res)))
        .catch((err) => console.log(err))
        .catch(() => {})
        .finally(() => {
          changeRequestStatus('groupsUser', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllCHMetrics = useCallback(
    (params: GetAllCHMetrics.Params) => {
      changeRequestStatus('statusCH', STATUSPAGE.LOADING);
      dashboardService
        .getAllCHMetrics(params)
        .then((res) => dispatch(loadCHMetrics(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusCH', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetSummaryProfile = useCallback(
    (params: GetSummaryProfile.Params) => {
      setLoadingSummaryProfile(STATUSPAGE.LOADING);
      dashboardService
        .getSummaryProfile(params)
        .then((res) => {
          dispatch(loadDashboardSummary(res));
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setLoadingSummaryProfile(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetCompletedAndMissedActivitiesCount = useCallback(
    (params: GetCompletedAndMissedActivitiesCount.Params) => {
      changeRequestStatus(
        'statusCompletedAndMissedActivitiesCount',
        STATUSPAGE.LOADING,
      );

      dashboardService
        .getCompletedAndMissedActivitiesCount(params)
        .then((res) => dispatch(loadCompletedAndMissedActivitiesCount(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus(
            'statusCompletedAndMissedActivitiesCount',
            STATUSPAGE.DONE,
          );
        });
    },
    [],
  );

  const requestGetActivitiesInProgressCount = useCallback(
    (params: GetActivitiesInProgressCount.Params) => {
      changeRequestStatus(
        'statusActivitiesInProgressCount',
        STATUSPAGE.LOADING,
      );

      dashboardService
        .getActivitiesInProgressCount(params)
        .then((res) => dispatch(loadActivitiesInProgressCount(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus(
            'statusActivitiesInProgressCount',
            STATUSPAGE.DONE,
          );
        });
    },
    [],
  );

  const requestGetAllUsersMetrics = useCallback(
    (params?: GetAllUsersMetrics.Params) => {
      dashboardService
        .getAllUsersMetrics(params)
        .then((res) => dispatch(loadUsersMetrics(res)))
        .catch((err) => console.log(err));
    },
    [],
  );

  const requestGetAllActivitiesByPlace = useCallback(
    (params: GetAllActivitiesByPlace.Params) => {
      changeRequestStatus('statusActivitiesByPlace', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByPlace(params)
        .then((res) => dispatch(loadActivitiesByPlace(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusActivitiesByPlace', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByTask = useCallback(
    (params: GetAllActivitiesByTask.Params) => {
      changeRequestStatus('statusActivitiesByTask', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByTask(params)
        .then((res) => dispatch(loadActivitiesByTask(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusActivitiesByTask', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByStudent = useCallback(
    (params: GetAllActivitiesByStudent.Params) => {
      changeRequestStatus('statusActivitiesByStudent', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByStudent(params)
        .then((res) => dispatch(loadActivitiesByStudent(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusActivitiesByStudent', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByGroup = useCallback(
    (params: GetAllActivitiesByGroup.Params) => {
      changeRequestStatus('statusActivitiesByGroup', STATUSPAGE.LOADING);

      dashboardService
        .getAllActivitiesByGroup(params)
        .then((res) => dispatch(loadActivitiesByGroup(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusActivitiesByGroup', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapActivitiesMarkers = useCallback(
    (params: GetMapActivitiesMarkers.Params) => {
      changeRequestStatus('statusMapActivitiesMarkers', STATUSPAGE.LOADING);

      dashboardService
        .getMapActivitiesMarkers(params)
        .then((res) => dispatch(loadMapActivitiesMarkers(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusMapActivitiesMarkers', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapActivityDetails = useCallback(
    (params: GetMapActivityDetails.Params) => {
      changeRequestStatus('statusMapActivityDetails', STATUSPAGE.LOADING);

      dashboardService
        .getMapActivityDetails(params)
        .then((res) => dispatch(loadMapActivityDetails(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusMapActivityDetails', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetMapInternshipMarkers = useCallback(
    (params: GetMapInternshipMarkers.Params) => {
      changeRequestStatus('statusMapInternshipMarkers', STATUSPAGE.LOADING);

      dashboardService
        .getMapInternshipMarkers(params)
        .then((res) => dispatch(loadMapInternshipMarkers(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('statusMapInternshipMarkers', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllActivitiesByStatus = useCallback(
    (params: GetAllActivitiesByStatus.Params) => {
      changeRequestStatus(
        'statusPageActivitiesAllByStatus',
        STATUSPAGE.LOADING,
      );
      return dashboardService
        .getAllActivitiesByStatus(params)
        .then((res) => dispatch(loadActivitiesByStatus(res)))
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus(
            'statusPageActivitiesAllByStatus',
            STATUSPAGE.DONE,
          );
        });
    },
    [],
  );

  const requestGetActivityByStatus = useCallback(
    (params: GetActivityByStatus.Params) => {
      dispatch(loadingStatusPageActivityByStatus(true));
      dashboardService
        .getActivitiesByStatus(params)
        .then((res: GetActivityByStatus.Result) =>
          dispatch(loadActivityByStatus(res)),
        )
        .catch((err) => console.log(err))
        .finally(() => {
          dispatch(loadingStatusPageActivityByStatus(false));
        });
    },
    [],
  );

  const requestListOnlineUsersPerOrganizationIndicators = useCallback(() => {
    changeRequestStatus(
      'statusDashboardIndicatorsUsersOnline',
      STATUSPAGE.LOADING,
    );
    dashboardService
      .listOnlineUsersDashboardIndicators()
      .then((res) => {
        setSaveOnlineUsersDashboardIndicators(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        changeRequestStatus(
          'statusDashboardIndicatorsUsersOnline',
          STATUSPAGE.DONE,
        );
      });
  }, []);

  const requestListLastRegistrationApp = useCallback(() => {
    changeRequestStatus('statusLastRegistrationApp', STATUSPAGE.LOADING);
    dashboardService
      .listLastRegistrationApp()
      .then((res) => {
        setSaveLastRegistrationApp(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        changeRequestStatus('statusLastRegistrationApp', STATUSPAGE.DONE);
      });
  }, []);

  const requestListUsersOnlinePerPlatform = useCallback(() => {
    changeRequestStatus('statusListUsersOnlinePerPlatform', STATUSPAGE.LOADING);
    dashboardService
      .listUsersOnlinePlatform()
      .then((res) => {
        setSaveUsersOnlinePerPlatform(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        changeRequestStatus(
          'statusListUsersOnlinePerPlatform',
          STATUSPAGE.DONE,
        );
      });
  }, []);

  const requestGetActivitiesInProgressCountGroupedByOrganization =
    useCallback(() => {
      changeRequestStatus(
        'statusGetActivitiesInProgressCountGroupedByOrganization',
        STATUSPAGE.LOADING,
      );
      activityService
        .getActivitiesInProgressCountGroupedByOrganization()
        .then((res) => {
          setResponseActivityListingInProgress(res);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          changeRequestStatus(
            'statusGetActivitiesInProgressCountGroupedByOrganization',
            STATUSPAGE.DONE,
          );
        });
    }, []);

  return {
    requestsStatus,
    organizationsUser,
    curricularUnitsUser,
    loadingSummaryProfile,
    saveLastRegistrationApp,
    saveUsersOnlinePerPlatform,
    responseActivityListingInProgress,
    saveOnlineUsersDashboardIndicators,
    requestGetAllCHMetrics,
    requestGetAllGroupsUser,
    requestGetAllCoursesUser,
    requestGetSummaryProfile,
    requestGetAllUsersMetrics,
    requestGetActivityByStatus,
    requestGetAllSemestersUser,
    requestGetAllActivitiesByTask,
    requestGetMapActivityDetails,
    requestGetMapActivitiesMarkers,
    requestGetMapInternshipMarkers,
    requestGetAllActivitiesByGroup,
    requestGetCompletedAndMissedActivitiesCount,
    requestGetActivitiesInProgressCount,
    requestListLastRegistrationApp,
    requestGetAllActivitiesByPlace,
    requestGetAllOrganizationsUser,
    requestGetAllActivitiesByStatus,
    requestGetAllCurricularUnitsUser,
    requestGetAllActivitiesByStudent,
    requestGetActivitiesInProgressCountGroupedByOrganization,
    requestListUsersOnlinePerPlatform,
    requestListOnlineUsersPerOrganizationIndicators,
  };
};
